import React, { useEffect, useMemo, useState } from "react";
import { Editor } from "@tinymce/tinymce-react";
import Dropdown from "@/components/ui/Dropdown";
import { emailService } from "@/services/emailService";
import { marketingCampaignsService } from "@/services/marketingCampaignsService";
import useToastStore from "@/components/Common/Toast/toastStore";

const TINYMCE_INIT = {
  height: 320,
  menubar: false,
  plugins: [
    "advlist",
    "autolink",
    "lists",
    "link",
    "image",
    "charmap",
    "preview",
    "anchor",
    "searchreplace",
    "visualblocks",
    "code",
    "fullscreen",
    "insertdatetime",
    "media",
    "table",
    "code",
    "help",
    "wordcount",
  ],
  toolbar:
    "undo redo | blocks | " +
    "bold italic forecolor | alignleft aligncenter " +
    "alignright alignjustify | bullist numlist outdent indent | " +
    "link | removeformat | help",
  content_style:
    "body { font-family: -apple-system, BlinkMacSystemFont, San Francisco, Segoe UI, Roboto, Helvetica Neue, sans-serif; font-size: 14px }",
  skin: "oxide",
  content_css: "default",
  branding: false,
  statusbar: true,
  resize: true,
  promotion: false,
};

const isOptedIn = (value) => value === true || value === 1 || value === "1";

const normalizeCampaign = (item) => ({
  id: item.id,
  name: item.name ?? item.title ?? `Campaign #${item.id}`,
  template_id: item.template_id ?? item.template?.id ?? null,
  channel:
    item.channel ?? item.template?.channel ?? item.template_channel ?? "email",
  subject: item.subject ?? item.template?.subject ?? item.name ?? "",
  email_body:
    item.email_body ?? item.body ?? item.template?.body ?? item.content ?? "",
  sms_body: item.sms_body ?? item.template?.sms_body ?? "",
});

const toPlainText = (html = "") =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const RequiredMark = () => <span className="text-red-500 ml-0.5">*</span>;

const MarketingComposeModal = ({
  isOpen,
  onClose,
  type = "email",
  selectedContacts = [],
  onSuccess,
}) => {
  const [campaignName, setCampaignName] = useState("");
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false);
  const { showToast } = useToastStore;

  const isEmail = type === "email";
  const title = isEmail ? "Send Email" : "Send SMS";
  const contentLabel = isEmail ? "Email Content" : "Message";
  const placeholder = isEmail
    ? "Write your email..."
    : "Write your SMS message...";
  const sendLabel = isEmail ? "Send Email" : "Send SMS";

  const campaignOptions = useMemo(
    () =>
      campaigns
        .filter((campaign) => {
          const channel = campaign.channel || "email";
          if (isEmail) {
            return channel === "email" || channel === "both";
          }
          return channel === "sms" || channel === "both";
        })
        .map((campaign) => ({
          value: String(campaign.id),
          label: campaign.name,
          subject: campaign.subject || campaign.name || "",
          body: isEmail ? campaign.email_body || "" : campaign.sms_body || "",
          template_id: campaign.template_id || null,
        })),
    [campaigns, isEmail],
  );

  const selectedCampaignOption =
    campaignOptions.find(
      (option) => String(option.value) === String(selectedCampaignId),
    ) || null;

  useEffect(() => {
    if (!isOpen) {
      setCampaignName("");
      setSubject("");
      setContent("");
      setIsSending(false);
      setSelectedCampaignId("");
      setCampaigns([]);
      return;
    }

    const loadCampaigns = async () => {
      try {
        setIsLoadingCampaigns(true);
        const response = await marketingCampaignsService.getCampaigns({
          per_page: 100,
        });
        const list = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.campaigns)
            ? response.campaigns
            : Array.isArray(response)
              ? response
              : [];
        setCampaigns(list.map(normalizeCampaign));
      } catch (error) {
        showToast({
          message: error.message || "Failed to load campaigns",
          type: "error",
        });
        setCampaigns([]);
      } finally {
        setIsLoadingCampaigns(false);
      }
    };

    loadCampaigns();
  }, [isOpen, showToast]);

  const handleCampaignChange = (selectedOption) => {
    const campaignId = selectedOption?.value || "";
    setSelectedCampaignId(campaignId);

    if (!campaignId) {
      setCampaignName("");
      return;
    }

    setCampaignName(selectedOption.label || "");
    if (isEmail) {
      setSubject(selectedOption.subject || "");
    }
    setContent(selectedOption.body || "");
  };

  const handleSend = async () => {
    if (!selectedContacts.length) {
      showToast({
        message: "Please select at least one contact",
        type: "error",
      });
      return;
    }

    if (!campaignName.trim()) {
      showToast({
        message: "Please enter a campaign name",
        type: "error",
      });
      return;
    }

    if (isEmail && !subject.trim()) {
      showToast({
        message: "Please enter an email subject",
        type: "error",
      });
      return;
    }

    if (!content.trim() || content.trim() === "<p><br></p>") {
      showToast({
        message: isEmail
          ? "Please enter email content"
          : "Please enter an SMS message",
        type: "error",
      });
      return;
    }

    const smsBody = !isEmail ? toPlainText(content) : "";
    if (!isEmail && smsBody.length > 1600) {
      showToast({
        message: "SMS message must be 1600 characters or fewer",
        type: "error",
      });
      return;
    }

    const optInKey = isEmail ? "email_opt_in" : "sms_opt_in";
    const candidates = selectedContacts
      .filter((contact) => isOptedIn(contact[optInKey]) && contact.user_id)
      .map((contact) => ({ id: contact.user_id }));

    if (!candidates.length) {
      showToast({
        message: isEmail
          ? "No email-opted-in contacts with a valid candidate id selected"
          : "No SMS-opted-in contacts with a valid candidate id selected",
        type: "error",
      });
      return;
    }

    const payload = {
      channel: isEmail ? "email" : "sms",
      candidates,
    };

    if (selectedCampaignId) {
      payload.campaign_id = Number(selectedCampaignId) || selectedCampaignId;
      if (selectedCampaignOption?.template_id) {
        payload.template_id = selectedCampaignOption.template_id;
      }
    }

    if (campaignName.trim()) {
      payload.campaign_name = campaignName.trim();
    }

    if (isEmail) {
      payload.subject = subject.trim();
      payload.body = content.trim();
    } else {
      payload.sms_body = smsBody;
    }

    setIsSending(true);

    try {
      const result = await emailService.sendNewsletter(payload);
      const queued = result?.queued ?? candidates.length;
      const skippedCount = Array.isArray(result?.skipped)
        ? result.skipped.length
        : 0;
      const channelLabel = isEmail ? "Email" : "SMS";

      let message =
        result?.status ||
        `${channelLabel} queued for ${queued} contact${queued === 1 ? "" : "s"}`;

      if (skippedCount > 0) {
        message += ` (${skippedCount} skipped)`;
      }

      showToast({
        message,
        type: queued > 0 ? "success" : "error",
      });

      if (queued > 0) {
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      showToast({
        message:
          error.message ||
          (isEmail ? "Failed to send email" : "Failed to send SMS"),
        type: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="marketing-page-scope fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div
          className="fixed inset-0 bg-foreground/40 transition-opacity"
          onClick={onClose}
        />
        <div className="relative transform overflow-hidden rounded-lg bg-card border border-border text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
          <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center gap-4 mb-4">
              <h3 className="text-lg font-medium text-foreground shrink-0">
                {title}
              </h3>

              <div className="min-w-0 w-full max-w-[220px] ml-auto">
                <Dropdown
                  className="w-full"
                  options={campaignOptions}
                  value={selectedCampaignOption}
                  placeholder={
                    isLoadingCampaigns ? "Loading..." : "Select Campaign"
                  }
                  onChange={handleCampaignChange}
                  searchable
                  isLoading={isLoadingCampaigns}
                  loadingText="Loading campaigns..."
                  disabled={isLoadingCampaigns}
                  triggerClassName="!bg-background !border !border-input hover:!border-primary"
                />
              </div>
            </div>

            <div className="mt-4">
              <label
                htmlFor="marketing-campaign-name"
                className="block text-sm font-medium text-foreground mb-2"
              >
                Campaign Name
                <RequiredMark />
              </label>
              <input
                type="text"
                id="marketing-campaign-name"
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                placeholder="Enter campaign name..."
              />
            </div>

            {isEmail && (
              <div className="mt-4">
                <label
                  htmlFor="marketing-email-subject"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  Subject
                  <RequiredMark />
                </label>
                <input
                  type="text"
                  id="marketing-email-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-input bg-background text-foreground placeholder:text-muted-foreground rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                  placeholder="Enter email subject..."
                />
              </div>
            )}

            <div className="mt-4">
              <label className="block text-sm font-medium text-foreground mb-2">
                {contentLabel}
                <RequiredMark />
              </label>
              <Editor
                apiKey={import.meta.env.VITE_TINYMCE_API_KEY}
                value={content}
                onEditorChange={(value) => setContent(value)}
                init={{
                  ...TINYMCE_INIT,
                  placeholder,
                }}
              />
            </div>
          </div>

          <div className="bg-muted px-4 py-3 gap-2 sm:flex sm:flex-row-reverse sm:px-6">
            <button
              type="button"
              onClick={handleSend}
              disabled={isSending}
              className="inline-flex w-full justify-center rounded-md bg-primary-dark text-white px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-input sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? "Sending..." : sendLabel}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isSending}
              className="inline-flex w-full justify-center rounded-md bg-card px-3 py-2 text-sm font-semibold text-foreground shadow-sm ring-1 ring-inset ring-input sm:w-auto disabled:opacity-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingComposeModal;
